import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import { CustomerContext } from 'context/customer';
import { SearchContext } from 'components/views/search/context/SearchContext';
import { logSelectRestaurantEvent } from 'helpers/mParticle';
import { getFromChownowApi } from 'helpers/api';
import { formatOutgoingQueryParams } from 'helpers/search';
import { SET_AUTOCOMPLETE_PANEL_ACTIVE } from 'components/views/search/reducer/index';

import Grid from 'components/designSystem/Grid/Grid.tsx';
import LoadingSpinner from 'components/designSystem/LoadingSpinner/LoadingSpinner';

import { PictoChinese56 } from 'components/shared/SVG/PictoChinese';

import AutocompleteRow from './AutocompleteRow/AutocompleteRow';

import styles from './AutocompleteResults.module.scss';

// eslint-disable-next-line complexity
export default function AutocompleteResults({ onAutocompleteSearch, onClickCuisine }) {
  const { user } = useContext(CustomerContext);
  const {
    state: { autoCompleteAPI, searchFiltersFromURL },
    dispatchToReducer,
  } = useContext(SearchContext);
  const { cuisines, restaurants } = autoCompleteAPI.results || {};
  const isAutocompleteLoading = autoCompleteAPI.isLoading;
  const showEmptyState = isEmpty(cuisines) && isEmpty(restaurants) && !isAutocompleteLoading;
  const showCuisines = !isEmpty(cuisines);
  const showRestaurants = !isEmpty(restaurants);

  async function handleRestaurantClick(item, index) {
    // Some results (like restaurants with multiple locations) do not have a restaurant id to generate the url,
    // we perform a search in that case
    if (item.id) {
      logSelectRestaurantEvent({
        cardSource: 'restaurant_search',
        index,
        restaurant: item,
        user,
      });
      const { data } = await getFromChownowApi(`restaurant/${item.id}`);
      const { origin } = window.location;

      const path = `${origin}/order/${data.company_id}/locations/${
        data.id
      }${formatOutgoingQueryParams(searchFiltersFromURL)}`;
      window.location.href = path;
    } else {
      onAutocompleteSearch(item.name);
      dispatchToReducer({ type: SET_AUTOCOMPLETE_PANEL_ACTIVE, payload: false });
    }
  }

  return (
    <Grid sm={4} md={12} lg={12}>
      {isAutocompleteLoading && <LoadingSpinner isCentered size="x-large" />}
      {showEmptyState && (
        <div className={styles.errorState}>
          <PictoChinese56 />
          <p className={styles.errorHeader}>0 results match your search</p>
          <p className={styles.errorText}>Please enter a new search</p>
        </div>
      )}
      {showCuisines && (
        <div className={styles.section}>
          <p className={styles.sectionHeader}>CUISINES</p>
          <ul className={styles.results}>
            {cuisines.map((item) => (
              <AutocompleteRow
                key={item.name}
                item={item}
                onAutocompleteClick={() => onClickCuisine(item.name)}
              />
            ))}
          </ul>
        </div>
      )}
      {showRestaurants && (
        <div className={styles.section}>
          <p className={styles.sectionHeader}>RESTAURANTS</p>
          <ul className={styles.results}>
            {restaurants.map((item, i) => (
              <AutocompleteRow
                key={item.name}
                item={item}
                onAutocompleteClick={() => handleRestaurantClick(item, i)}
              />
            ))}
          </ul>
        </div>
      )}
    </Grid>
  );
}

AutocompleteResults.propTypes = {
  onAutocompleteSearch: PropTypes.func.isRequired,
  onClickCuisine: PropTypes.func.isRequired,
};
